// src/contexts/CompanyContext.js - UPDATED with comprehensive logging
import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth.js';
import companyService from '../services/companyService.js';

export const CompanyContext = createContext();

export const CompanyProvider = ({ children }) => {
  const { currentUser, isAuthenticated, employeeType } = useAuth();
  
  const [userCompanies, setUserCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user's companies when authenticated
  useEffect(() => {
    const fetchUserCompanies = async () => {
      console.log('=== 🏢 CompanyContext: STARTING company fetch ===');
      console.log('   - isAuthenticated:', isAuthenticated);
      console.log('   - currentUser:', currentUser);
      console.log('   - currentUser?.isEmployee:', currentUser?.isEmployee);
      console.log('   - currentUser?.memberId:', currentUser?.memberId);
      
      // Only fetch if user is authenticated AND is an employee AND has a memberId
      if (!isAuthenticated || !currentUser?.isEmployee || !currentUser?.memberId) {
        console.log('   ❌ Not fetching companies - conditions not met:');
        console.log('      - isAuthenticated:', isAuthenticated);
        console.log('      - isEmployee:', currentUser?.isEmployee);
        console.log('      - has memberId:', !!currentUser?.memberId);
        
        setUserCompanies([]);
        setSelectedCompany(null);
        setCompanyDetails(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log('   📡 Calling companyService.getMemberCompanies...');
        const companies = await companyService.getMemberCompanies(currentUser.memberId);
        
        console.log('   ✅ Companies fetched successfully');
        console.log('      - Number of companies:', companies?.length || 0);
        console.log('      - Companies:', companies);
        
        setUserCompanies(companies);
        
        // Set the first company as selected by default if none selected
        if (companies.length > 0 && !selectedCompany) {
          console.log('   🎯 Setting first company as selected:', companies[0]);
          setSelectedCompany(companies[0]);
          
          // Fetch full details for the selected company
          console.log('   📡 Fetching company details for company:', companies[0].companyId);
          await fetchCompanyDetails(companies[0].companyId);
        } else if (companies.length === 0) {
          console.log('   ⚠️ No companies found for this user');
          setSelectedCompany(null);
          setCompanyDetails(null);
        } else {
          console.log('   ℹ️ Company already selected:', selectedCompany);
        }
      } catch (err) {
        console.error('   ❌ Failed to fetch user companies:', err);
        console.error('      - Error name:', err.name);
        console.error('      - Error message:', err.message);
        console.error('      - Error stack:', err.stack);
        
        setError(err.message);
        setUserCompanies([]);
        setSelectedCompany(null);
        setCompanyDetails(null);
      } finally {
        console.log('   ✅ Finished fetching companies, setting loading to false');
        setLoading(false);
      }
    };

    fetchUserCompanies();
  }, [isAuthenticated, currentUser?.isEmployee, currentUser?.memberId]);

  // Fetch full company details including permissions
  const fetchCompanyDetails = useCallback(async (companyId) => {
    console.log('=== 📋 CompanyContext: FETCHING company details ===');
    console.log('   - companyId:', companyId);
    
    if (!companyId) {
      console.log('   ❌ No companyId provided, skipping fetch');
      return;
    }

    try {
      console.log('   📡 Calling companyService.getCompanyById...');
      const details = await companyService.getCompanyById(companyId);
      
      console.log('   ✅ Company details fetched successfully');
      console.log('      - Company name:', details.companyName);
      console.log('      - Company ID:', details.companyId);
      
      // Extract and log all permission fields
      const permissions = Object.entries(details)
        .filter(([key]) => key.startsWith('admin'))
        .reduce((acc, [key, val]) => ({ ...acc, [key]: val }), {});
      
      console.log('      - Permissions:', permissions);
      console.log('      - Permission count:', Object.keys(permissions).length);
      console.log('      - Enabled permissions:', 
        Object.entries(permissions)
          .filter(([_, val]) => val === true)
          .map(([key]) => key)
      );
      
      setCompanyDetails(details);
      console.log('   ✅ Company details STATE UPDATED');
      
    } catch (err) {
      console.error('   ❌ Failed to fetch company details:', err);
      console.error('      - Error name:', err.name);
      console.error('      - Error message:', err.message);
      console.error('      - Error stack:', err.stack);
      setCompanyDetails(null);
    }
  }, []);

  // Change selected company
  const changeSelectedCompany = useCallback(async (companyId) => {
    console.log('=== 🔄 CompanyContext: CHANGING selected company ===');
    console.log('   - New companyId:', companyId);
    console.log('   - Current companyId:', selectedCompany?.companyId);
    
    const company = userCompanies.find(c => c.companyId === companyId);
    
    if (company) {
      console.log('   ✅ Company found in user companies:', company);
      setSelectedCompany(company);
      localStorage.setItem('selectedCompanyId', companyId.toString());
      console.log('   💾 Saved to localStorage');
      
      // Fetch full details for the newly selected company
      console.log('   📡 Fetching details for new company...');
      await fetchCompanyDetails(companyId);
    } else {
      console.log('   ❌ Company not found in user companies');
      console.log('      - Available company IDs:', userCompanies.map(c => c.companyId));
    }
  }, [userCompanies, fetchCompanyDetails]);

  // Refresh companies list
  const refreshCompanies = useCallback(async () => {
    console.log('=== 🔄 CompanyContext: REFRESHING companies ===');
    
    if (!isAuthenticated || !currentUser?.isEmployee || !currentUser?.memberId) {
      console.log('   ❌ Cannot refresh - user not authenticated or not employee');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('   📡 Fetching updated company list...');
      const companies = await companyService.getMemberCompanies(currentUser.memberId);
      console.log('   ✅ Companies refreshed:', companies?.length || 0);
      
      setUserCompanies(companies);
      
      if (companies.length > 0 && !selectedCompany) {
        console.log('   🎯 Setting first company as selected');
        setSelectedCompany(companies[0]);
        await fetchCompanyDetails(companies[0].companyId);
      }
      
      if (selectedCompany) {
        const stillExists = companies.find(c => c.companyId === selectedCompany.companyId);
        
        if (stillExists) {
          console.log('   ✅ Current company still exists, refreshing details');
          setSelectedCompany(stillExists);
          await fetchCompanyDetails(stillExists.companyId);
        } else {
          console.log('   ⚠️ Current company no longer exists, selecting new one');
          const newSelected = companies[0] || null;
          setSelectedCompany(newSelected);
          
          if (newSelected) {
            await fetchCompanyDetails(newSelected.companyId);
          } else {
            setCompanyDetails(null);
          }
        }
      }
    } catch (err) {
      console.error('   ❌ Failed to refresh companies:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, currentUser?.isEmployee, currentUser?.memberId, selectedCompany, fetchCompanyDetails]);

  // Get company details (cached or fresh fetch)
  const getCompanyDetails = useCallback(async (companyId) => {
    console.log('=== 🔍 CompanyContext: GET company details ===');
    console.log('   - Requested companyId:', companyId);
    console.log('   - Selected companyId:', selectedCompany?.companyId);
    console.log('   - Have cached details:', !!companyDetails);
    
    // If asking for currently selected company, return cached details
    if (companyId === selectedCompany?.companyId && companyDetails) {
      console.log('   ✅ Returning cached details');
      return companyDetails;
    }
    
    // Otherwise fetch fresh details
    try {
      console.log('   📡 Fetching fresh details...');
      const company = await companyService.getCompanyById(companyId);
      console.log('   ✅ Fresh details fetched');
      return company;
    } catch (err) {
      console.error('   ❌ Failed to fetch company details:', err);
      throw err;
    }
  }, [selectedCompany, companyDetails]);

  // Update company
  const updateCompany = useCallback(async (companyId, companyData) => {
    console.log('=== 💾 CompanyContext: UPDATING company ===');
    console.log('   - companyId:', companyId);
    console.log('   - Update data:', companyData);
    
    try {
      const updatedCompany = await companyService.updateCompany(companyId, companyData);
      console.log('   ✅ Company updated:', updatedCompany);
      
      setUserCompanies(prevCompanies =>
        prevCompanies.map(company =>
          company.companyId === companyId
            ? { ...company, companyName: updatedCompany.companyName }
            : company
        )
      );
      console.log('   ✅ User companies list updated');
      
      if (selectedCompany?.companyId === companyId) {
        console.log('   🎯 Updated company is selected, updating state');
        setSelectedCompany(prev => ({
          ...prev,
          companyName: updatedCompany.companyName
        }));
        
        // Refresh full company details after update
        console.log('   📡 Refreshing company details...');
        await fetchCompanyDetails(companyId);
      }
      
      return updatedCompany;
    } catch (err) {
      console.error('   ❌ Failed to update company:', err);
      throw err;
    }
  }, [selectedCompany, fetchCompanyDetails]);

  // Create company
  const createCompany = useCallback(async (companyData) => {
    console.log('=== ➕ CompanyContext: CREATING company ===');
    console.log('   - Company data:', companyData);
    
    try {
      const newCompany = await companyService.createCompany(companyData);
      console.log('   ✅ Company created:', newCompany);
      
      await refreshCompanies();
      return newCompany;
    } catch (err) {
      console.error('   ❌ Failed to create company:', err);
      throw err;
    }
  }, [refreshCompanies]);

  // Delete company
  const deleteCompany = useCallback(async (companyId) => {
    console.log('=== 🗑️ CompanyContext: DELETING company ===');
    console.log('   - companyId:', companyId);
    
    try {
      await companyService.deleteCompany(companyId);
      console.log('   ✅ Company deleted');
      
      setUserCompanies(prevCompanies =>
        prevCompanies.filter(company => company.companyId !== companyId)
      );
      
      if (selectedCompany?.companyId === companyId) {
        console.log('   🎯 Deleted company was selected, selecting new one');
        const remainingCompanies = userCompanies.filter(c => c.companyId !== companyId);
        const newSelected = remainingCompanies[0] || null;
        setSelectedCompany(newSelected);
        
        if (newSelected) {
          await fetchCompanyDetails(newSelected.companyId);
        } else {
          setCompanyDetails(null);
        }
      }
    } catch (err) {
      console.error('   ❌ Failed to delete company:', err);
      throw err;
    }
  }, [selectedCompany, userCompanies, fetchCompanyDetails]);

  // Load selected company from localStorage on mount
  useEffect(() => {
    const storedCompanyId = localStorage.getItem('selectedCompanyId');
    console.log('=== 💾 CompanyContext: Checking localStorage ===');
    console.log('   - Stored companyId:', storedCompanyId);
    console.log('   - User companies loaded:', userCompanies.length);
    
    if (storedCompanyId && userCompanies.length > 0) {
      const company = userCompanies.find(c => c.companyId === parseInt(storedCompanyId, 10));
      
      if (company && !selectedCompany) {
        console.log('   ✅ Restoring company from localStorage:', company);
        setSelectedCompany(company);
        fetchCompanyDetails(company.companyId);
      } else if (!company) {
        console.log('   ⚠️ Stored company not found in user companies');
      }
    }
  }, [userCompanies, fetchCompanyDetails]);

  // Helper function to check if user has specific permission
  const hasPermission = useCallback((permissionKey) => {
    console.log('🔐 CompanyContext - hasPermission check');
    console.log('   - permissionKey:', permissionKey);
    console.log('   - employeeType:', employeeType);
    console.log('   - companyDetails exists:', !!companyDetails);
    
    // Admin always has all permissions
    if (employeeType === 1) {
      console.log('   ✅ Admin - access granted');
      return true;
    }
    
    // Customer service checks company permissions
    if (employeeType === 2 && companyDetails) {
      const hasAccess = companyDetails[permissionKey] === true;
      console.log('   📋 Customer service - checking permission');
      console.log('      - Permission value:', companyDetails[permissionKey]);
      console.log('      - Has access:', hasAccess);
      return hasAccess;
    }
    
    console.log('   ❌ No employee type or company details - access denied');
    console.log('      - employeeType:', employeeType);
    console.log('      - companyDetails:', companyDetails);
    return false;
  }, [employeeType, companyDetails]);

  // Get all permissions as an object
  const getPermissions = useCallback(() => {
    console.log('📋 CompanyContext - getPermissions called');
    console.log('   - employeeType:', employeeType);
    console.log('   - companyDetails exists:', !!companyDetails);
    
    if (!companyDetails) {
      console.log('   ⚠️ No company details - returning empty permissions');
      return {};
    }

    // Admin has all permissions
    if (employeeType === 1) {
      console.log('   ✅ Admin - returning all permissions as true');
      return {
        adminAccessCabinetTypes: true,
        adminAccessCabinetFrameTypes: true,
        adminAccessItems: true,
        adminAccessDoors: true,
        adminAccessDrawerboxes: true,
        adminAccessDrawerSlides: true,
        adminAccessFinish: true,
        adminAccessMaterials: true,
        adminAccessHardware: true,
        adminAccessSettings: true,
        adminAccessEmployees: true,
        adminAccessPricingModel: true,
        adminAccessCustomers: true,
        adminAccessHourlyRates: true,
        adminAccessLabor: true,
        adminAccessBrushingProcess: true,
        adminAccessLocations: true,
        adminAccessFreight: true,
        adminAccessInstallation: true,
        adminAccessStylePresets: true
      };
    }
    
    // Customer service gets permissions from company details
    console.log('   📊 Customer service - extracting permissions from company details');
    const permissions = {
      adminAccessCabinetTypes: companyDetails.adminAccessCabinetTypes || false,
      adminAccessCabinetFrameTypes: companyDetails.adminAccessCabinetFrameTypes || false,
      adminAccessItems: companyDetails.adminAccessItems || false,
      adminAccessDoors: companyDetails.adminAccessDoors || false,
      adminAccessDrawerboxes: companyDetails.adminAccessDrawerboxes || false,
      adminAccessDrawerSlides: companyDetails.adminAccessDrawerSlides || false,
      adminAccessFinish: companyDetails.adminAccessFinish || false,
      adminAccessMaterials: companyDetails.adminAccessMaterials || false,
      adminAccessHardware: companyDetails.adminAccessHardware || false,
      adminAccessSettings: companyDetails.adminAccessSettings || false,
      adminAccessEmployees: companyDetails.adminAccessEmployees || false,
      adminAccessPricingModel: companyDetails.adminAccessPricingModel || false,
      adminAccessCustomers: companyDetails.adminAccessCustomers || false,
      adminAccessHourlyRates: companyDetails.adminAccessHourlyRates || false,
      adminAccessLabor: companyDetails.adminAccessLabor || false,
      adminAccessBrushingProcess: companyDetails.adminAccessBrushingProcess || false,
      adminAccessLocations: companyDetails.adminAccessLocations || false,
      adminAccessFreight: companyDetails.adminAccessFreight || false,
      adminAccessInstallation: companyDetails.adminAccessInstallation || false,
      adminAccessStylePresets: companyDetails.adminAccessStylePresets || false
    };
    
    console.log('   📋 Permissions extracted:', permissions);
    console.log('      - Enabled count:', Object.values(permissions).filter(v => v === true).length);
    
    return permissions;
  }, [employeeType, companyDetails]);

  const value = {
    userCompanies,
    selectedCompany,
    companyDetails,
    loading,
    error,
    changeSelectedCompany,
    refreshCompanies,
    getCompanyDetails,
    updateCompany,
    createCompany,
    deleteCompany,
    hasPermission,
    getPermissions
  };

  console.log('🎨 CompanyContext render - Current state:', {
    userCompaniesCount: userCompanies.length,
    selectedCompanyId: selectedCompany?.companyId,
    selectedCompanyName: selectedCompany?.companyName,
    companyDetailsLoaded: !!companyDetails,
    loading,
    error,
    employeeType
  });

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};